/**
 * pincodeGeo — the ONE web pincode → city/state lookup (2026-09-24).
 *
 * Lifted verbatim out of `pages/crm/LeadsPage.tsx`, which has called
 * `GET /geo/pincode/:code` since the Add-lead form shipped. The dealer forms
 * need the same behaviour, and the plan is explicit: reuse this lookup, do not
 * write a second one. LeadsPage now calls through here too, so there is one
 * implementation and one place a change to it lands.
 *
 * 🔒 THE GAZETTEER RETURNS A DISTRICT, NOT A CITY (spec A10). What fills the
 * City field is the DISTRICT, and the field stays freely editable. Calling it
 * a city in the payload would be the lie; calling it a city on screen is what
 * the rep expects to type into.
 *
 * 🔒 AN UNKNOWN PINCODE FILLS NOTHING AND BLOCKS NOTHING. `found: false` is a
 * normal answer, never an error: the rep types both fields and saves.
 */
import { api } from '@/utils/api';

export interface PincodeLookup {
  found: boolean;
  /** The gazetteer's DISTRICT, which is what the City field is filled with. */
  city: string;
  state: string;
  /** What to show under the field. Empty when there is nothing to say. */
  label: string;
}

export const PINCODE_NOT_FOUND = 'Unknown pincode — type the city and state yourself';
export const PINCODE_LOOKUP_FAILED = 'Lookup failed — type the city and state yourself';

/** Digits only, at most six — the shape every pincode input is normalised to. */
export function normalizePincode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

export function isCompletePincode(value: string): boolean {
  return /^\d{6}$/.test(value);
}

/**
 * Ask the gazetteer. Never throws: a provider that is down must not stop a rep
 * saving a dealer, so a failure comes back as `found: false` with its own line.
 */
export async function lookupPincode(pin: string): Promise<PincodeLookup> {
  try {
    const res = await api.get(`/geo/pincode/${pin}`);
    const d = (res as { data?: { data?: { district?: unknown; state?: unknown } | null } })?.data?.data;
    if (d && (d.district || d.state)) {
      const city = String(d.district ?? '');
      const state = String(d.state ?? '');
      return { found: true, city, state, label: [city, state].filter(Boolean).join(', ') };
    }
    return { found: false, city: '', state: '', label: PINCODE_NOT_FOUND };
  } catch {
    return { found: false, city: '', state: '', label: PINCODE_LOOKUP_FAILED };
  }
}
